import { createElement } from "commonAssets/lib/createElement.js";

/**
 * createOfficialBanner()
 *
 * Creates the "official settlement website" banner element for the home page.
 * Text resolution priority:
 *   1. page.PageLead — CMS-managed, per-site, per-language (rendered as HTML)
 *   2. bannerConfig.content[lang] — per-language config override
 *   3. options.officialBannerText — legacy config string
 *   4. i18n official_banner_text — generic fallback with {SiteName}/{CourtName}
 *
 * @param {Object} params
 * @param {Object} params.i18n - The i18n instance (with getTranslation and userLanguage)
 * @param {Object} params.siteInfoLang - Current-language site info from CMS
 * @param {Object} params.appDomain - App domain config (contains options)
 * @param {string} [params.pageLead] - PageLead HTML content from CMS
 * @returns {HTMLElement|string} The banner section element, or empty string if disabled
 */
export function createOfficialBanner({
    i18n,
    siteInfoLang,
    appDomain,
    pageLead,
}) {
    const options = appDomain?.options || {};
    const bannerConfig = options.officialBanner;

    // Determine if banner should display
    const shouldDisplay =
        typeof bannerConfig === "object"
            ? bannerConfig.display !== false
            : bannerConfig !== false;

    if (!shouldDisplay) return "";

    const currentLang = i18n.userLanguage || "en";
    const siteName = siteInfoLang?.SiteName || "";
    const courtName = siteInfoLang?.CourtName || "";
    const ariaLabel = i18n.getTranslation("official_banner_aria");

    // Resolve banner content (highest to lowest priority):
    // 1. PageLead from CMS — render as HTML
    if (pageLead) {
        return createElement(
            "section",
            {
                id: "official-settlement-website-hero",
                class: "mb-4",
                role: "note",
                "aria-label": ariaLabel,
            },
            [
                createElement(
                    "div",
                    {
                        class: "p-3 p-md-4 text-center rounded-3 border bg-body-tertiary",
                    },
                    [],
                    pageLead
                ),
            ]
        );
    }

    // 2. Per-language config override
    // 3. Legacy config string (officialBannerText)
    // 4. i18n translation with placeholders
    let bannerText;
    if (
        typeof bannerConfig === "object" &&
        bannerConfig.content?.[currentLang]
    ) {
        bannerText = bannerConfig.content[currentLang];
    } else if (options.officialBannerText) {
        bannerText = options.officialBannerText;
    } else {
        bannerText = i18n
            .getTranslation("official_banner_text")
            .replace("{SiteName}", siteName)
            .replace("{CourtName}", courtName);
    }

    return createElement(
        "section",
        {
            id: "official-settlement-website-hero",
            class: "mb-4",
            role: "note",
            "aria-label": ariaLabel,
        },
        [
            createElement(
                "div",
                {
                    class: "p-3 p-md-4 text-center rounded-3 border bg-body-tertiary",
                },
                [
                    createElement(
                        "p",
                        { class: "mb-0 fs-5 fw-semibold text-body" },
                        [],
                        bannerText
                    ),
                ]
            ),
        ]
    );
}
